import { DateTime } from 'luxon';
import { DateScale } from './types';

export interface TimelineData {
  headerDates: { offset: number; date: number }[];
  headerMonths: {
    offset: number;
    month: string;
    year: number;
    newPeriod: boolean;
    monthIndex: number;
  }[];
  today: { offset: number; date: number };
  timelineStart: DateTime;
  totalWeeks: number;
  dateHeaderScale: number;
}

const dateScaleConfig = {
  month: {
    dateHeaderScale: 36,
    isNewPeriod: () => true,
    // Display every day
    includeDay: () => true,
  },
  quarter: {
    dateHeaderScale: 10,
    isNewPeriod: (month: number) => month % 3 === 1,
    // Display every Monday
    includeDay: (date: DateTime) => date.weekday === 1,
  },
  year: {
    dateHeaderScale: 4,
    isNewPeriod: (month: number) => month === 1,
    // Display every other Monday
    includeDay: (date: DateTime) =>
      date.weekday === 1 && date.weekNumber % 2 === 0,
  },
} as const;

export const BASE_OFFSET = 4;

export const timelineData = (
  _startDate: DateTime<true>,
  dateScale: DateScale,
): TimelineData => {
  const headerDates: TimelineData['headerDates'] = [];
  const headerMonths: TimelineData['headerMonths'] = [];
  const { dateHeaderScale, isNewPeriod, includeDay } =
    dateScaleConfig[dateScale];

  const now = DateTime.now();
  const startDate = _startDate >= now ? now : _startDate;
  // the first date to display, which is always the beginning of either the current month,
  // or the month of the oldest block
  const firstDisplay = startDate.startOf('month');
  // total number of days to render
  const totalDays = now
    .plus({ years: 1 })
    .endOf('month')
    .diff(firstDisplay)
    .as('days');

  // loop through every day between the first date and a year from now
  // depending on the date scale, add a date marker to be rendered
  // for each new month, add a month marker to be rendered
  let currentMonth: number = firstDisplay.minus({ month: 1 }).month;
  for (let dayOffset = 0; dayOffset < totalDays; dayOffset++) {
    const date = firstDisplay.plus({ days: dayOffset });
    if (includeDay(date)) {
      headerDates.push({
        offset: dayOffset + BASE_OFFSET,
        date: date.day,
      });
    }
    if (currentMonth !== date.month) {
      currentMonth = date.month;
      const offset = date.set({ day: 1 }).diff(firstDisplay).as('days');

      headerMonths.push({
        offset: offset + BASE_OFFSET,
        month: date.monthShort,
        year: date.year,
        newPeriod: isNewPeriod(currentMonth),
        monthIndex: currentMonth,
      });
    }
  }

  const timelineStart = firstDisplay.minus({ days: BASE_OFFSET });
  const today = {
    offset: Math.floor(now.diff(firstDisplay, 'days').as('days')) + BASE_OFFSET,
    date: now.day,
  };

  return {
    headerDates,
    headerMonths,
    today,
    timelineStart,
    totalWeeks: Math.ceil(totalDays / 7),
    dateHeaderScale: dateHeaderScale,
  };
};
